import type { LoaderResponse, ViewResponse } from "@bestowinc/tinkertoy-core";
import {
  LoaderResponseSchema,
  LOADER_NDJSON_MEDIA_TYPE,
} from "@bestowinc/tinkertoy-core";
import { redirect } from "react-router";

import {
  FAILURE_EVENT_TAGS,
  isViewLoadError,
  markFailureReported,
  reportFailureOnce,
  resolveFailureError,
} from "../error-telemetry";
import type { LoadingSlot } from "../loading/loading-slot";
import { getViewLoadPromise } from "../view-loader/view-read-resource";
import {
  PERFORMANCE_METRIC_NAMES,
  startPerformanceTimer,
} from "../performance-telemetry";
import type { PerformanceTimer } from "../performance-telemetry";
import { consumeLoaderStream } from "./consume-loader-stream";
import { ShellLoaderError } from "./shell-loader-error";
import type { ShellConfig } from "./types";

function throwShellLoaderFailure(init: ConstructorParameters<typeof ShellLoaderError>[0]): never {
  const error = new ShellLoaderError(init);
  reportFailureOnce(error, {
    tag: FAILURE_EVENT_TAGS.shellLoader,
    errorCode: error.code,
    message: error.message,
    error,
    moduleId: error.errorContext?.moduleId,
  });
  throw error;
}

function isJsonResponse(response: Response): boolean {
  const contentType = response.headers.get("content-type") ?? "";
  return contentType.includes("application/json");
}

function isNdjsonResponse(response: Response): boolean {
  const contentType = response.headers.get("content-type") ?? "";
  return contentType.includes(LOADER_NDJSON_MEDIA_TYPE);
}

async function finalizeLoaderResponse(
  parsed: LoaderResponse,
  timer: PerformanceTimer,
  viewLoader: ShellLoaderConfig["viewLoader"],
  extra?: { assetsReceivedMs?: number },
): Promise<ViewResponse | Response> {
  switch (parsed.type) {
    case "redirect":
      if (parsed.external) {
        timer.complete("success", extra);
        window.location.assign(parsed.location);
        return new Promise<never>(() => undefined);
      }
      timer.complete("success", extra);
      return redirect(parsed.location);

    case "error":
      timer.complete("failure", {
        ...extra,
        errorCode: parsed.code,
        moduleId: parsed.errorContext?.moduleId,
      });
      return throwShellLoaderFailure({
        message: parsed.message,
        status: parsed.status,
        code: parsed.code,
        errorView: parsed.errorView,
        errorContext: parsed.errorContext,
        flowMeta: parsed.flowMeta,
        errorModuleData: parsed.errorModuleData,
      });

    case "view": {
      // Navigation blocks on the view bundle, so the timer covers it too.
      const { viewId, viewVersion } = parsed.loadedView;
      try {
        await awaitViewBundle(parsed, viewLoader);
      } catch (err) {
        timer.complete("failure", {
          ...extra,
          errorCode: "views_load_failed",
          viewId,
          viewVersion,
        });
        throw err;
      }
      timer.complete("success", { ...extra, viewId, viewVersion });
      return parsed;
    }
  }
}

/**
 * Waits for the view bundle (already primed from the assets chunk on the
 * streaming path) so the shell can render the view on the same commit as its
 * data. A failed import surfaces through the route's error boundary, reported
 * once as a view-bundle failure.
 */
async function awaitViewBundle(
  response: ViewResponse,
  viewLoader: ShellLoaderConfig["viewLoader"],
): Promise<ViewResponse> {
  const { viewId, viewVersion } = response.loadedView;
  try {
    await getViewLoadPromise(viewLoader, viewId, viewVersion);
  } catch (err) {
    const shellError = new ShellLoaderError({
      message: `Failed to load view "${viewId}" (${viewVersion}).`,
      status: 500,
      code: "views_load_failed",
      errorContext: { errorType: "views_load_failed" },
      flowMeta: response.flowMeta,
    });
    if (isViewLoadError(err)) {
      const failureError = resolveFailureError(err, shellError.message);
      reportFailureOnce(failureError, {
        tag: FAILURE_EVENT_TAGS.viewBundleLoad,
        errorCode: err.code,
        message: failureError.message,
        error: failureError,
        viewId: err.viewId,
        viewVersion: err.version,
      });
      markFailureReported(shellError);
    }
    throw shellError;
  }
  return response;
}

async function parseLegacyLoaderResponse(
  response: Response,
  timer: PerformanceTimer,
): Promise<LoaderResponse> {
  if (!isJsonResponse(response)) {
    timer.complete("failure", {
      errorCode: "non_json_response",
    });
    throwShellLoaderFailure({
      message: response.ok
        ? "Server returned a non-JSON response."
        : response.statusText || `Server returned ${response.status}.`,
      status: response.ok ? 502 : response.status,
      code: "non_json_response",
    });
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    timer.complete("failure", {
      errorCode: "non_json_response",
    });
    throwShellLoaderFailure({
      message: "Server returned malformed JSON.",
      status: 502,
      code: "non_json_response",
    });
  }

  const result = LoaderResponseSchema.safeParse(body);
  if (!result.success) {
    timer.complete("failure", {
      errorCode: "invalid_response_shape",
    });
    throwShellLoaderFailure({
      message: "Unexpected server response format.",
      status: 502,
      code: "invalid_response_shape",
    });
  }

  return result.data;
}

/**
 * Creates a React Router loader that fetches view data from the Tinkertoy server.
 *
 * The loader is the single point of contact between the client and the server.
 * On every navigation, React Router calls this loader with the current URL.
 * The loader fetches the corresponding view data and handles the three possible
 * response types: view (return data), redirect (follow client-side, or
 * full-document navigate when `external`), error (throw {@link ShellLoaderError}).
 *
 * When the server negotiates {@link LOADER_NDJSON_MEDIA_TYPE}, the loader primes
 * view and module CDN imports from the early assets chunk while waiting for the
 * terminal data payload. Legacy servers that respond with a single JSON body
 * continue to use the original path with no early priming.
 *
 * The browser's full URL (origin + path + search) is forwarded as a query
 * parameter so the server can reconstruct the original request context —
 * hostname for host-based flow matching, pathname for path/branch/view
 * resolution, and search params for downstream use.
 *
 * Cancellation is automatic — React Router passes `request.signal`, which aborts
 * the in-flight fetch when a new navigation supersedes the current one.
 */
/**
 * Loader function type for the Tinkertoy shell route. Consumers should use
 * this with React Router's typed `useLoaderData<ShellLoader>()` to get
 * compile-time type safety tied to {@link createShellLoader}'s return type,
 * instead of casting an `unknown`.
 */
export type ShellLoader = ReturnType<typeof createShellLoader>;

export type ShellLoaderConfig = Pick<
  ShellConfig,
  "apiBaseUrl" | "viewLoader" | "moduleLoader"
> & {
  /** Receives streamed loading metadata while this loader run is in flight. */
  loadingSlot?: LoadingSlot;
};

export function createShellLoader(config: ShellLoaderConfig) {
  return async ({
    request,
  }: {
    request: Request;
  }): Promise<ViewResponse | Response> => {
    const slot = config.loadingSlot;
    const token = slot?.begin();
    let redirected = false;
    try {
      const result = await load(request, token);
      redirected = result instanceof Response;
      return result;
    } finally {
      if (slot && token !== undefined) {
        slot.end(token, { redirected });
      }
    }
  };

  async function load(
    request: Request,
    token: number | undefined,
  ): Promise<ViewResponse | Response> {
    const timer = startPerformanceTimer(PERFORMANCE_METRIC_NAMES.loadNavigation, {});
    const fetchStartedAt = performance.now();

    const serverUrl = `${config.apiBaseUrl}/load?url=${encodeURIComponent(
      request.url,
    )}`;

    let response: Response;
    try {
      response = await fetch(serverUrl, {
        signal: request.signal,
        headers: {
          Accept: `${LOADER_NDJSON_MEDIA_TYPE}, application/json`,
        },
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        timer.complete("cancelled");
        throw err;
      }
      timer.complete("failure", {
        errorCode: "network_error",
      });
      throwShellLoaderFailure({
        message: err instanceof Error ? err.message : "Network request failed.",
        status: 0,
        code: "network_error",
      });
    }

    if (isNdjsonResponse(response)) {
      let streamed: Awaited<ReturnType<typeof consumeLoaderStream>>;
      try {
        streamed = await consumeLoaderStream(
          response,
          request.signal,
          {
            viewLoader: config.viewLoader,
            moduleLoader: config.moduleLoader,
          },
          fetchStartedAt,
          payload => {
            if (token !== undefined) {
              config.loadingSlot?.update(token, payload);
            }
          },
        );
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          timer.complete("cancelled");
          throw err;
        }
        if (request.signal.aborted) {
          timer.complete("cancelled");
          throw request.signal.reason ?? new DOMException("Aborted", "AbortError");
        }
        if (err instanceof Error) {
          timer.complete("failure", {
            errorCode: "invalid_response_shape",
          });
          throwShellLoaderFailure({
            message: err.message,
            status: 502,
            code: "invalid_response_shape",
          });
        }
        throw err;
      }
      // A terminal-only stream never reported; say so before the view bundle wait.
      if (token !== undefined) {
        config.loadingSlot?.reportNoLoading(token);
      }
      // Outside the stream `try`: a server error terminal throws its own
      // ShellLoaderError here, which must not be rewritten as a malformed
      // stream.
      return await finalizeLoaderResponse(
        streamed.terminal.response,
        timer,
        config.viewLoader,
        { assetsReceivedMs: streamed.assetsReceivedMs },
      );
    }

    if (token !== undefined) {
      config.loadingSlot?.reportNoLoading(token);
    }
    const parsed = await parseLegacyLoaderResponse(response, timer);
    return await finalizeLoaderResponse(parsed, timer, config.viewLoader);
  };
}
